import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, EventEmitter, signal, Directive, HostListener, HostBinding, Input, ViewChild, Output, inject } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { Subject, BehaviorSubject, distinctUntilChanged, filter, map, concatMap, takeUntil, of, combineLatest, startWith, tap, take, switchMap, delay, auditTime } from 'rxjs';
import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';

/**
 * A token to provide the necessary configuration to the tour service
 */
const NgxTourStepToken = new InjectionToken('NgxTourStepToken');

/**
 * Determines whether an element is visible in the viewport.
 * It calculates the position that should be scrolled to, taking into account the provided offset and
 * the relative position of the element to the center of the viewport.s
 *
 * @param element - The provided element
 * @param offset - The optional configurable margin around the cutout
 */
const elementIsVisibleInViewport = (element, margin, offset) => {
    if (!element) {
        // Wouter: If element is not provided, return "true" as to prevent scrolling
        return { isVisible: true, scrollY: undefined, relativeTo: 'top' };
    }
    const { top, bottom, height: elementHeight } = element.getBoundingClientRect();
    const { innerHeight, scrollY } = window;
    const isVisible = top >= 0 && bottom <= innerHeight;
    // Wouter: If the vertical center of the element is on top of the vertical center of the viewport, the element is closer to the top
    const isCloserToTop = top + elementHeight / 2 <= innerHeight / 2;
    // Wouter: If element is in viewport, return true
    if (isVisible) {
        // Wouter: Take the required offset into account
        if (isCloserToTop) {
            return { isVisible: true, scrollY: scrollY - (offset?.top || 0), relativeTo: 'top' };
        }
        return { isVisible: true, scrollY: scrollY + (offset?.bottom || 0), relativeTo: 'bottom' };
    }
    // Wouter: If element is not in viewport, calculate whether it is closer to top or bottom.
    if (isCloserToTop) {
        // Wouter: If closer to top, set scrollY to top of viewport and the provided offset
        return {
            isVisible: false,
            scrollY: top - margin - (offset?.top || 0),
            relativeTo: 'top',
        };
    }
    // Wouter: If closer to bottom, set scrollY to bottom of viewport with an offset of the cutout height and the provided offset
    return {
        isVisible: false,
        scrollY: bottom - innerHeight + margin + (offset?.bottom || 0),
        relativeTo: 'bottom',
    };
};

/**
 * A singleton service used to run help tours through an application.
 */
class NgxTourService {
    constructor(cdkOverlayService, platformId, configuration) {
        this.cdkOverlayService = cdkOverlayService;
        this.platformId = platformId;
        this.configuration = configuration;
        /**
         * A subject to hold the destroyed event
         */
        this.destroyedSubject = new Subject();
        /**
         * A subject to hold the window resize event
         */
        this.windowResizeSubject = new Subject();
        /**
         * A subject to hold the backdrop clip event
         */
        this.backdropClipEventSubject = new Subject();
        /**
         * A record of registered step elements we wish to highlight
         */
        this.elements = {};
        /**
         * A record of positions to place the
         */
        this.positionMap = {
            below: { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
            above: { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
            left: { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom' },
            right: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'bottom' },
        };
        /**
         * The amount of steps of the current tour
         */
        this.amountOfSteps = 0;
        /**
         * The currently active step in the tour as a subject
         */
        this.currentStepSubject = new BehaviorSubject(undefined);
        /**
         * The previously active step in the tour as a subject
         */
        this.previousStepSubject = new BehaviorSubject(undefined);
        /**
         * The index of the current step of the tour as a subject
         */
        this.currentIndexSubject = new BehaviorSubject(0);
        /**
         * Whether the tour has started as a subject
         */
        this.tourStartedSubject = new Subject();
        /**
         * Whether the tour has ended as a subject
         */
        this.tourEndedSubject = new Subject();
        /**
         * The currently active tour as a subject
         */
        this.currentTourSubject = new BehaviorSubject(undefined);
        /**
         * A subject to hold the registration events
         */
        this.registerElementSubject = new Subject();
        /**
         * The currently active tour
         */
        this.currentTour$ = this.currentTourSubject.asObservable();
        /**
         * The currently active step
         */
        this.currentStep$ = this.currentStepSubject
            .asObservable()
            .pipe(distinctUntilChanged(), filter(Boolean));
        /**
         * The currently active index
         */
        this.currentIndex$ = this.currentIndexSubject
            .asObservable()
            .pipe(distinctUntilChanged());
        /**
         * The previously active step
         */
        this.previousStep$ = this.previousStepSubject
            .asObservable()
            .pipe(distinctUntilChanged(), filter(Boolean));
        /**
         * Whether the tour has ended
         */
        this.tourEnded$ = this.tourEndedSubject.asObservable();
        /**
         * Whether the tour has started
         */
        this.tourStarted$ = this.tourStartedSubject.asObservable();
        /**
         * Whether the tour is active
         */
        this.tourActive$ = this.currentStepSubject
            .asObservable()
            .pipe(map(Boolean));
        // Iben: We use a subject with concatMap here because we want each event to be handled correctly and the elements record should finish updating before updating it again.
        this.registerElementSubject
            .pipe(filter(Boolean), concatMap((event) => {
            return this.handleRegistrationEvent(event);
        }), takeUntil(this.destroyedSubject))
            .subscribe();
        // Iben: Listen to the onresize event of the window
        this.runInBrowser(({ browserWindow }) => {
            browserWindow.onresize = () => {
                this.windowResizeSubject.next();
            };
        });
    }
    /**
     * Start a provided tour
     *
     * @param tour - The tour we wish to start
     * @param onClose - An optional on close function we wish to run
     * @param startIndex - An optional index we wish to start the tour from , default this is 0
     */
    startTour(tour, onClose, startIndex = 0) {
        // Wouter: Early exit if the tour is run on the server, for which this service is not intended
        if (isPlatformServer(this.platformId)) {
            console.warn('NgxTourService: The tour service is not intended to be run on the server. The tour will not be started.');
            return of(null);
        }
        this.runInBrowser(({ browserWindow, browserDocument }) => {
            // Iben: Save the current scroll position so we can return to it when we close the tour
            this.startingScrollPosition = browserWindow.scrollY;
            // Iben: Save the current
            this.bodyOverflow = browserDocument.body.style.overflow;
        });
        // Iben: Loop over the tour and set subjects for all elements that haven't been found yet
        tour.forEach((step) => {
            if (!this.elements[step.tourItem]) {
                this.elements[step.tourItem] = new BehaviorSubject(undefined);
            }
        });
        // Iben: Set the new tour
        this.currentTourSubject.next(tour);
        // Iben: Set the amount of steps based on the length of the tour
        this.amountOfSteps = tour.length;
        // Iben: Set the first two starting subjects
        this.currentIndexSubject.next(startIndex);
        this.tourStartedSubject.next();
        // Wouter: Set a class onto the body to indicate onto which styling can be added by the user
        this.handleBodyClass('set');
        // Iben: Listen to the window resize and the backdrop clip event, so that when the window resizes, the clip path still works correctly
        combineLatest([
            this.windowResizeSubject.pipe(startWith(undefined)),
            this.backdropClipEventSubject,
        ])
            .pipe(tap(([, event]) => {
            // Iben: Set the clip path based on the event
            this.setClipPath(event);
        }), takeUntil(this.tourEnded$))
            .subscribe();
        // Iben: Start the first tour, and run it until the tour is ended
        this.setStep(tour[startIndex]).pipe(takeUntil(this.tourEnded$)).subscribe();
        // Iben: Listen to the end of the tour and run the end function when needed
        return this.tourEndedSubject.asObservable().pipe(take(1), switchMap(() => this.runStepFunction(onClose)), tap(() => {
            this.runInBrowser(({ browserWindow, browserDocument }) => {
                // Iben: Scroll back to the starting position
                browserWindow.scrollTo({ top: this.startingScrollPosition });
                // Iben: Restore the body overflow
                browserDocument.body.style.overflow = this.bodyOverflow;
            });
        }), map(() => undefined));
    }
    /**
     * Registers a template element so it can be highlighted
     *
     * @param element - The highlighted element
     */
    registerElement(element) {
        this.registerElementSubject.next({
            tourItem: element.tourItem,
            element,
            type: 'register',
        });
    }
    /**
     * Removes an element from the record
     *
     * @param tourItem - The id of the element
     */
    unregisterElement(tourItem) {
        this.registerElementSubject.next({
            tourItem,
            type: 'unregister',
        });
    }
    /**
     * Closes the currently running tour
     */
    closeTour() {
        // Iben: Remove the current overlay
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = undefined;
        }
        // Iben: Set the current and previous item to inactive
        this.elements[this.currentStepSubject.getValue()?.tourItem]?.getValue()?.setActive(false);
        this.elements[this.previousStepSubject.getValue()?.tourItem]?.getValue()?.setActive(false);
        // Iben: Reset the subjects
        this.currentStepSubject.next(undefined);
        this.previousStepSubject.next(undefined);
        // Iben: Reset the tour and the tour length
        this.currentTourSubject.next(undefined);
        this.amountOfSteps = 0;
        // Wouter: Remove the body class
        this.handleBodyClass('remove');
        // Iben: Emit the end of the tour
        this.tourEndedSubject.next();
        // Iben: Return an empty Observable
        return of(null);
    }
    ngOnDestroy() {
        // Iben: Complete all subscriptions
        this.destroyedSubject.next();
        this.destroyedSubject.complete();
        this.tourEndedSubject.next();
        this.tourEndedSubject.complete();
        this.windowResizeSubject.next();
        this.windowResizeSubject.complete();
        // Wouter: Remove the body class
        this.handleBodyClass('remove');
        // Iben: Get rid of the onresize event
        this.runInBrowser(({ browserWindow }) => {
            browserWindow.onresize = null;
        });
    }
    /**
     * Sets a provided step in the tour
     *
     * @param currentStep - The provided step we want to set
     */
    setStep(currentStep) {
        // Iben: Get the previous step, if it exists
        const previousStep = this.currentStepSubject.getValue();
        const previousItem = this.elements[previousStep?.tourItem]?.getValue();
        // Iben: Set the previous item back to inactive
        if (previousItem) {
            previousItem.setActive(false);
        }
        // Iben: Early exit and close tour if there's no current step
        if (!currentStep) {
            return this.closeTour();
        }
        // Iben: Run the afterVisible of the previous step
        return this.runStepFunction(previousStep?.afterVisible).pipe(
        // Iben: Run the beforeVisible of the current step
        concatMap(() => {
            return this.runStepFunction(currentStep.beforeVisible);
        }), 
        // Wouter: Add a delay to allow the change detection to be ran before the step is visualized.
        // This makes sure the bounding box of the highlighted element is correct.
        delay(1), concatMap(() => {
            // Iben: If no tourItem was provided, we render the step in the center of the page
            if (!currentStep.tourItem) {
                return this.handleStepInteractions(currentStep);
            }
            // Iben: If a tourItem was provided, we know we have to search for the element
            return this.elements[currentStep.tourItem].pipe(
            // Iben: Check if the item already exists. If not, we wait and grab the latest emit using auditTime
            // We do this to avoid a delay when going back and forth in the tour
            concatMap((item) => {
                return item
                    ? of(item)
                    : this.elements[currentStep.tourItem].pipe(
                    // Iben: If no delay was provided, we use the default of 100ms
                    auditTime(currentStep.delay || 100));
            }), take(1), switchMap((item) => {
                // Iben: If an item wasn't found, we move in the current direction until we find an element
                if (!item) {
                    return this.handleNext(this.currentDirection || 'next');
                }
                // Iben: If the item was found, we visualize the step
                return this.handleStepInteractions(currentStep, item);
            }));
        }));
    }
    /**
     * Handles the next action in the tour
     *
     * @param direction - The direction we wish to move in
     */
    handleNext(direction) {
        // Iben set the current direction so we know in which way we're moving the tour
        this.currentDirection = direction;
        // Iben: Get the new index
        const index = direction === 'next'
            ? this.currentIndexSubject.getValue() + 1
            : this.currentIndexSubject.getValue() - 1;
        // Iben: Update the new index
        this.currentIndexSubject.next(index);
        // Iben: Set the next step in the tour
        return this.setStep(this.currentTourSubject.getValue()[index]);
    }
    /**
     * Visualizes the current step
     *
     * @private
     * @param currentStep - The step we wish to visualize
     * @param item - The item we wish to highlight
     */
    visualizeStep(currentStep, item) {
        return this.runInBrowser(({ browserDocument, browserWindow }) => {
            // Iben: Update the previous and current step subject
            this.previousStepSubject.next(this.currentStepSubject.getValue());
            this.currentStepSubject.next(currentStep);
            // Iben: Restore the body overflow so we can scroll to the right element
            browserDocument.body.style.overflow = this.bodyOverflow;
            // Iben: Calculate the defaultOffsets
            // Wouter: This needs to happen before we calculate the scroll position
            const margin = this.getCutoutMargin(currentStep);
            // Iben: Scroll to the top before each step to get consistent behavior when going back and forth
            browserWindow.scrollTo({ top: 0 });
            const { isVisible, scrollY, relativeTo } = elementIsVisibleInViewport(item?.elementRef?.nativeElement, margin, { ...this.configuration.offset, ...currentStep?.offset });
            // Iben: Scroll to the element if it's not in view
            if (!isVisible) {
                browserWindow.scrollTo({ top: scrollY });
            }
            // Iben: Disable scrolling
            browserDocument.body.style.overflow = 'hidden';
            // Wouter: Calculate the step's position so that it is rendered relatively to the cutout
            const relativeDisplay = relativeTo == 'bottom' ? 'above' : 'below';
            const positionY = currentStep.position ? currentStep.position : relativeDisplay;
            const offsetY = positionY === 'above' ? -margin : margin;
            const offsetX = currentStep.position === 'right' ? margin : -margin;
            // Iben: Determine the position of the item based on whether a tourItem was provided
            const positionStrategy = item
                ? this.cdkOverlayService
                    .position()
                    .flexibleConnectedTo(item.elementRef)
                    .withDefaultOffsetY(offsetY)
                    .withDefaultOffsetX(offsetX)
                    .withPositions([this.positionMap[currentStep.position || relativeDisplay]])
                : this.cdkOverlayService
                    .position()
                    .global()
                    .centerHorizontally()
                    .centerVertically();
            // Iben: Create an overlay if it does not exist
            if (!this.overlayRef) {
                // Iben: Create an overlay config
                const config = new OverlayConfig({
                    hasBackdrop: !currentStep.disableBackDrop,
                    // Iben: Due to issues with how the scrollingStrategy.block() works with scrolling to items that are not in view, we set this to noop
                    scrollStrategy: this.cdkOverlayService.scrollStrategies.noop(),
                    positionStrategy,
                });
                // Create the overlay
                this.overlayRef = this.cdkOverlayService.create(config);
            }
            else {
                // Iben: Detach the previous portal
                this.overlayRef.detach();
                // Iben: If the overlay exists, we update the position strategy
                this.overlayRef.updatePositionStrategy(positionStrategy);
                // Iben: If the current step has no backdrop, we detach the backdrop
                if (currentStep.disableBackDrop) {
                    this.overlayRef.detachBackdrop();
                }
            }
            // Iben: Create a portal and attach the component
            const portal = new ComponentPortal(
            // Iben: If the currentStep has its own component, we overwrite it
            currentStep.component || this.configuration.component);
            const componentRef = this.overlayRef.attach(portal);
            const component = componentRef.instance;
            // Iben: Update the data of the component
            component.content = currentStep.content;
            component.title = currentStep.title;
            component.data = currentStep.data;
            component.currentStep = this.currentIndexSubject.getValue();
            component.amountOfSteps = this.amountOfSteps;
            component.position = item ? currentStep.position || 'below' : undefined;
            component.stepClass = currentStep.stepClass;
            component.elementId = item?.elementId;
            // Iben: Highlight the current html item as active if one is provided
            if (item) {
                // Iben: Set the active class of the item
                item.setActive(true);
            }
            // Iben: Set the clip path of the backdrop
            this.backdropClipEventSubject.next({
                backdrop: this.overlayRef.backdropElement,
                item: item?.elementRef?.nativeElement,
                cutoutMargin: this.getCutoutMargin(currentStep),
            });
            // Iben: Return the new component
            return componentRef;
        });
    }
    /**
     * Handles the interactions with a step
     *
     * @param  currentStep - The provided step
     * @param  item - An optional item
     */
    handleStepInteractions(currentStep, item) {
        // Iben: If the item was found, we visualize the step
        const componentRef = this.visualizeStep(currentStep, item);
        return this.runStepFunction(currentStep.onVisible).pipe(concatMap(() => {
            // Iben: Listen to the component interactions and respond accordingly
            return componentRef.instance.handleInteraction.pipe(take(1), concatMap((interaction) => {
                return interaction === 'close'
                    ? this.closeTour()
                    : this.handleNext(interaction);
            }));
        }));
    }
    /**
     * Returns an observable based on whether a step function was provided
     *
     * @param stepFunction - The provided step function
     */
    runStepFunction(stepFunction) {
        // Iben: If no step function was provided, we simply return a single void of
        if (!stepFunction) {
            return of(null);
        }
        // Iben: Run the step function and depending on the result, we return the result or an of.
        return (stepFunction(this.currentStepSubject.getValue(), this.currentIndexSubject.getValue()) ||
            of(null)).pipe(
        // Iben: We add a take(1) as an extra safety measure in case users end up not taking care of this
        take(1));
    }
    /**
     * Surrounds the provided item with a cutout in the backdrop
     *
     * @private
     * @param backdrop - The provided backdrop
     * @param item - The item we wish to surround
     * @param cutoutMargin - The amount of margin we want around the item
     */
    setClipPath(event) {
        const { backdrop, item, cutoutMargin } = event;
        // Iben: Early exit in case no backdrop or item is present
        if (!backdrop || !item) {
            return;
        }
        // Iben: Get the positions of the path around the item
        const { top, left, bottom, right } = item.getBoundingClientRect();
        const positionTop = top - cutoutMargin;
        const positionLeft = left - cutoutMargin;
        const positionBottom = bottom + cutoutMargin;
        const positionRight = right + cutoutMargin;
        // Iben: Create the box around the item
        const box = `${positionLeft}px ${positionTop}px, ${positionRight}px ${positionTop}px, ${positionRight}px ${positionBottom}px, ${positionLeft}px ${positionBottom}px, ${positionLeft}px ${positionTop}px`;
        // Iben: Clip the box out of the backdrop
        backdrop.style.clipPath = `polygon(evenodd, 0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,${box})`;
    }
    /**
     * Handles the registration event
     *
     * @param event - The registration event we wish to handle
     */
    handleRegistrationEvent(event) {
        // Iben: Early exit if no event was found
        if (!event) {
            return of(null);
        }
        // Iben: Unregister the element if it's an unregister event
        if (event.type === 'unregister') {
            this.elements[event.tourItem]?.next(undefined);
            return of(null);
        }
        // Iben: Early exit if no element was found
        if (!event.element) {
            return of(null);
        }
        // Iben: Check if there is already an item with the current id. If it is, update it, if not, make a new BehaviorSubject
        const elementSubject = this.elements[event.tourItem];
        // Iben: If we have an element subject already, we check if it already has an element
        if (elementSubject) {
            // Iben: If the current value is undefined, we register the element
            if (elementSubject.getValue() === undefined) {
                elementSubject.next(event.element);
            }
            return of(null);
        }
        // Iben: If no BehaviorSubject exists, we create one
        this.elements[event.tourItem] = new BehaviorSubject(event.element);
        return of(null);
    }
    /**
     * Returns the provided cutoutMargin of a step, or 5px if none is provided
     *
     * @param step - The current step
     */
    getCutoutMargin(step) {
        return step?.cutoutMargin === undefined ? 5 : step.cutoutMargin;
    }
    /**
     * Sets or removes the body class to indicate the tour is active
     */
    handleBodyClass(action) {
        this.runInBrowser(({ browserDocument }) => {
            if (action === 'set') {
                browserDocument.body.classList.add('ngx-tour-active');
            }
            else {
                browserDocument.body.classList.remove('ngx-tour-active');
            }
        });
    }
    //TODO: Iben: Remove this function in service of the WindowService once it is shared
    runInBrowser(callback) {
        if (isPlatformBrowser(this.platformId)) {
            return callback({ browserWindow: document.defaultView, browserDocument: document });
        }
        return undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourService, deps: [{ token: i1.Overlay }, { token: PLATFORM_ID }, { token: NgxTourStepToken }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NgxTourStepToken]
                }] }] });

/**
 * An abstract class that defines the minimum properties needed for the step component to be rendered
 */
class NgxTourStepComponent {
    /**
     * Close the tour on escape pressed
     */
    onEscape() {
        this.tourService.closeTour().subscribe();
    }
    ngOnInit() {
        // Iben: We set the correct host class. As this step is rendered and not changed afterwards, we do not have to adjust this in the onChanges
        const positionClass = this.position ? `ngx-tour-step-position-${this.position}` : '';
        this.rootClass = `ngx-tour-step ${positionClass} ${this.stepClass || ''}`;
    }
    ngAfterViewInit() {
        // Iben: If no title element was found, we throw an error
        if (!this.titleElement) {
            console.error('NgxTourService: The tour step component does not have an element marked with `stepTitle`. Because of that, the necessary accessibility attributes could not be set. Please add the `stepTitle` tag to the element that represents the title of the step.');
            return;
        }
        // Iben: Connect the aria-labbledby tag to the title element
        let id = this.titleElement.nativeElement.getAttribute('id');
        // Iben: If the title element does not have an id, we generate one
        if (!id) {
            id = UUID.UUID();
            this.titleElement.nativeElement.setAttribute('id', id);
        }
        // Iben: To prevent issues with changeDetection, we use a signal here to update the id
        this.titleId.set(id);
    }
    constructor(tourService) {
        this.tourService = tourService;
        /**
         * Emits the possible interactions with a step
         */
        this.handleInteraction = new EventEmitter();
        /**
         * The aria-labelledby id of the title element
         */
        this.titleId = signal('');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourStepComponent, deps: [{ token: NgxTourService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxTourStepComponent, isStandalone: false, inputs: { elementId: "elementId", position: "position", title: "title", content: "content", currentStep: "currentStep", amountOfSteps: "amountOfSteps", data: "data", stepClass: "stepClass" }, outputs: { handleInteraction: "handleInteraction" }, host: { attributes: { "role": "dialog" }, listeners: { "document:keydown.escape": "onEscape()" }, properties: { "attr.aria-modal": "true", "attr.aria-labelledby": "titleId()", "class": "this.rootClass", "attr.aria-details": "this.elementId" } }, viewQueries: [{ propertyName: "titleElement", first: true, predicate: ["stepTitle"], descendants: true }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourStepComponent, decorators: [{
            type: Directive,
            args: [{
                    host: {
                        role: 'dialog',
                        '[attr.aria-modal]': 'true',
                        '[attr.aria-labelledby]': 'titleId()',
                    },
                    standalone: false,
                }]
        }], ctorParameters: () => [{ type: NgxTourService }], propDecorators: { onEscape: [{
                type: HostListener,
                args: ['document:keydown.escape']
            }], rootClass: [{
                type: HostBinding,
                args: ['class']
            }], elementId: [{
                type: HostBinding,
                args: ['attr.aria-details']
            }, {
                type: Input,
                args: [{ required: true }]
            }], titleElement: [{
                type: ViewChild,
                args: ['stepTitle']
            }], position: [{
                type: Input,
                args: [{
                        required: true,
                    }]
            }], title: [{
                type: Input,
                args: [{ required: true }]
            }], content: [{
                type: Input,
                args: [{ required: true }]
            }], currentStep: [{
                type: Input,
                args: [{ required: true }]
            }], amountOfSteps: [{
                type: Input,
                args: [{ required: true }]
            }], data: [{
                type: Input
            }], stepClass: [{
                type: Input
            }], handleInteraction: [{
                type: Output
            }] } });

/**
 * A directive to mark elements in the DOM to be highlighted during a tour
 */
class NgxTourItemDirective {
    constructor(elementRef, tourService, cdRef) {
        this.elementRef = elementRef;
        this.tourService = tourService;
        this.cdRef = cdRef;
        /**
         * A class added to the currently active item
         */
        this.isActive = false;
    }
    /**
     * Mark an element as active or inactive
     *
     * @param isActive - Whether or not the element should be active
     */
    setActive(isActive) {
        // Iben: Mark the current item as active or inactive
        this.isActive = isActive;
        // Iben: Detect the changes on the component
        this.cdRef.detectChanges();
    }
    /**
     * Returns the id of the element. Uses for the `aria-details` on the tour-item component
     */
    get elementId() {
        return this.elementRef.nativeElement.getAttribute('id');
    }
    ngAfterViewInit() {
        // Iben: Register the element when rendered
        this.tourService.registerElement(this);
        // Iben: Check if the element has an id, if not, give it a new id for accessibility
        if (!this.elementRef.nativeElement.getAttribute('id')) {
            this.elementRef.nativeElement.setAttribute('id', UUID.UUID());
        }
    }
    ngOnDestroy() {
        // Iben: Unregister the element when the element gets destroyed
        this.tourService.unregisterElement(this.tourItem);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourItemDirective, deps: [{ token: i0.ElementRef }, { token: NgxTourService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxTourItemDirective, isStandalone: true, selector: "[tourItem]", inputs: { tourItem: "tourItem" }, host: { properties: { "class.ngx-tour-item-active": "this.isActive" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxTourItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tourItem]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: NgxTourService }, { type: i0.ChangeDetectorRef }], propDecorators: { isActive: [{
                type: HostBinding,
                args: ['class.ngx-tour-item-active']
            }], tourItem: [{
                type: Input
            }] } });

/**
 * Provides the configuration for the NgxDisplayContent directive. This can be either just
 * the component or the component and the offset. More information can be found in the
 * documentation of the `NgxTourStep`.
 *
 * @param configuration - The required configuration
 */
const provideNgxTourConfiguration = (configuration) => {
    return {
        provide: NgxTourStepToken,
        useValue: 
        // Wouter: If the configuration is an object, return it as is. If it is a component, return it as an object with the component as the `component` property and a default offset.
        typeof configuration === 'object'
            ? configuration
            : {
                component: configuration,
                offset: { top: 0, bottom: 0 },
            },
    };
};

/**
 * An operator to map the data of an Observable to mock data when the NgxTourService has an active tour.
 *
 * *Important*: This operator only works within an injection context
 *
 * @param mockData - The mock data we wish to use when the tour is active
 */
const useMockDataDuringTour = (mockData) => {
    // Iben: Inject the tour service
    const tourService = inject(NgxTourService);
    // Iben: Listen to the active state of the tour
    return switchMap((value) => {
        return combineLatest([of(value), tourService.tourActive$]).pipe(map(([value, isActive]) => {
            // Iben: If the tour is active, return the mockData, else, return the value
            if (isActive) {
                return mockData;
            }
            return value;
        }));
    });
};

/**
 * Generated bundle index. Do not edit.
 */

export { NgxTourItemDirective, NgxTourService, NgxTourStepComponent, provideNgxTourConfiguration, useMockDataDuringTour };
